"use client";

import type {
  HandoffCommand,
  AgentChoice,
  OsChoice,
} from "./agent-handoff-command";
import * as stylex from "@stylexjs/stylex";
import { styles } from "./agent-handoff.styles";
import { SegmentedControl } from "@softmax/design-system";
import { CommandCopy } from "../command-copy";
import { TerminalBlock } from "./terminal-command";
import { StalledHelp } from "../stalled-help";

type AgentHandOffViewProps = Readonly<{
  command: HandoffCommand;
  hydrated: boolean;
  continueCommand: string;
  stallAfterMs: number;
  onChoose({
    agent,
    os,
  }: Readonly<{ agent: AgentChoice; os: OsChoice | null }>): void;
  onCopy(text: string): Promise<void>;
  onContinueCopy(text: string): Promise<void>;
}>;

const choices = {
  "claude-posix": {
    value: "claude-posix",
    label: "Claude · Mac/Linux",
    agent: "claude",
    os: "posix",
  },
  "claude-windows": {
    value: "claude-windows",
    label: "Claude · Windows",
    agent: "claude",
    os: "windows",
  },
  "codex-posix": {
    value: "codex-posix",
    label: "Codex · Mac/Linux",
    agent: "codex",
    os: "posix",
  },
  "codex-windows": {
    value: "codex-windows",
    label: "Codex · Windows",
    agent: "codex",
    os: "windows",
  },
  other: { value: "other", label: "Other agent", agent: "other", os: null },
} as const;

export function AgentHandOffView({
  command,
  hydrated,
  continueCommand,
  stallAfterMs,
  onChoose,
  onCopy,
  onContinueCopy,
}: AgentHandOffViewProps) {
  const selected =
    command.kind === "chat"
      ? "other"
      : (`${command.agent}-${command.os}` as const);
  return (
    <div
      data-agent-hand-off
      {...stylex.props(styles.stack, !hydrated && localStyles.hidden)}
    >
      <SegmentedControl
        value={selected}
        items={Object.values(choices)}
        aria-label="Your agent"
        onValueChange={(value) => {
          const choice = choices[value];
          onChoose({ agent: choice.agent, os: choice.os });
        }}
      />
      {command.kind === "chat" ? (
        <div {...stylex.props(styles.stack)}>
          <CommandCopy command={command.prompt} onCopy={onCopy} />
          <p {...stylex.props(styles.prose, styles.paragraph)}>
            <span>
              <span {...stylex.props(styles.emphasis)}>
                Paste it into your agent’s chat
              </span>
              , then send. The sign-in code works once, for 5 minutes after you
              copy; copy again for a fresh one.
            </span>
          </p>
        </div>
      ) : (
        <TerminalBlock command={command} onCopy={onCopy} />
      )}
      <div data-agent-next {...stylex.props(styles.prose)}>
        <p {...stylex.props(styles.paragraph)}>
          <span {...stylex.props(styles.emphasis)}>What happens next.</span>{" "}
          Your agent works through the steps in order and prints a line after
          each: <span {...stylex.props(styles.keyboard)}>Step 1 done: …</span>.
          This page moves on by itself once your agent uploads your player.
        </p>
        <p {...stylex.props(styles.paragraph)}>
          <span {...stylex.props(styles.emphasis)}>
            A line starting HANDOFF:
          </span>{" "}
          means your agent needs you to do something (sign in, start Docker,
          turn on network access). Do that, then tell it to continue. It will
          not guess or retry on its own.
        </p>
      </div>
      <StalledHelp afterMs={stallAfterMs} summary="Nothing happening?">
        <p {...stylex.props(styles.paragraph)}>
          Look at your agent’s window. If it says “Not authenticated”, run{" "}
          <span {...stylex.props(styles.keyboard)}>uv run softmax login</span>{" "}
          in that folder; if it can’t open the link or reach Softmax, turn on
          network access for it. Then paste this into the same window:
        </p>
        <CommandCopy command={continueCommand} onCopy={onContinueCopy} />
      </StalledHelp>
    </div>
  );
}

const localStyles = stylex.create({
  hidden: { visibility: "hidden" },
});
